/** Demo Mode only — backs the Control Areas page (Stage 7 Compliance & Controls).
 * The 12 control areas are a fixed taxonomy: Create/Delete are intentionally not offered
 * on the page (see ControlAreas/index.tsx), only Update of thresholds/rules/evidence/escalation. */

export type ControlAreaName =
  | 'Stock'
  | 'Ordering'
  | 'Labour'
  | 'Food Safety'
  | 'Allergens'
  | 'Nutrition'
  | 'Waste'
  | 'Quality'
  | 'Pricing'
  | 'Hospitality'
  | 'Events'
  | 'Customer Experience';

export interface ControlArea {
  id: string;
  name: ControlAreaName;
  description: string;
  requiredChecks: string[];
  rulesOrThresholds: string[];
  evidenceRequirements: string[];
  escalationRules: string[];
  linkedTasks: string[];
  linkedAudits: string[];
  linkedActionItems: string[];
  reportingMetrics: string[];
}

export const controlAreasSeed: ControlArea[] = [
  {
    id: 'CA-STOCK',
    name: 'Stock',
    description: 'Stock Controls monitor stock counts, variances, adjustments, wastage impact, opening stock and readiness.',
    requiredChecks: ['Periodic stock count', 'Opening stock readiness check', 'Adjustment review'],
    rulesOrThresholds: ['Variance tolerance ±3% by value', 'High-value SKU recount trigger above ±1%'],
    evidenceRequirements: ['Signed stock count sheet', 'System variance report'],
    escalationRules: ['Escalate to Area Manager if variance exceeds tolerance 2 periods running'],
    linkedTasks: ['Weekly stock count task'],
    linkedAudits: ['Stock Control Audit'],
    linkedActionItems: ['Stock variance investigation'],
    reportingMetrics: ['Variance %', 'Adjustment count', 'Wastage value'],
  },
  {
    id: 'CA-ORDERING',
    name: 'Ordering',
    description: 'Ordering Controls monitor order completion, missed orders, supplier issues, substitutions, delivery failures and catalogue exceptions.',
    requiredChecks: ['Order completion check', 'Substitution approval check'],
    rulesOrThresholds: ['Missed order threshold: 0 tolerance for core menu lines', 'Substitution requires manager sign-off'],
    evidenceRequirements: ['Order confirmation record', 'Substitution approval note'],
    escalationRules: ['Escalate to Procurement if supplier delivery failure recurs within 30 days'],
    linkedTasks: ['Daily order confirmation task'],
    linkedAudits: ['Ordering Compliance Audit'],
    linkedActionItems: ['Supplier issue follow-up'],
    reportingMetrics: ['Missed order count', 'Substitution rate', 'Delivery failure rate'],
  },
  {
    id: 'CA-LABOUR',
    name: 'Labour',
    description: 'Labour Controls monitor role coverage, training gaps, certification expiry, shift coverage and readiness.',
    requiredChecks: ['Shift coverage check', 'Certification expiry check'],
    rulesOrThresholds: ['Minimum 1 certified food handler per shift', 'Certification renewal alert 30 days before expiry'],
    evidenceRequirements: ['Shift roster', 'Certification record'],
    escalationRules: ['Escalate to HR if certified cover unavailable for a scheduled shift'],
    linkedTasks: ['Rota readiness check'],
    linkedAudits: ['Labour Readiness Audit'],
    linkedActionItems: ['Training gap remediation'],
    reportingMetrics: ['Certification expiry count', 'Coverage gap count'],
  },
  {
    id: 'CA-FOOD-SAFETY',
    name: 'Food Safety',
    description: 'Food Safety Controls monitor temperature, cleaning, hygiene checks, incidents, food handling and evidence.',
    requiredChecks: ['Fridge/freezer temperature check', 'Cleaning schedule check', 'Food handling observation'],
    rulesOrThresholds: ['Chilled storage must remain below 5°C', 'Frozen storage must remain below -18°C'],
    evidenceRequirements: ['Temperature log', 'Cleaning checklist', 'Incident report'],
    escalationRules: ['Escalate immediately and block service if temperature breach exceeds 2 hours'],
    linkedTasks: ['Temperature check task'],
    linkedAudits: ['Food Safety Audit'],
    linkedActionItems: ['Temperature failure corrective action'],
    reportingMetrics: ['Temperature breach count', 'Cleaning check completion rate'],
  },
  {
    id: 'CA-ALLERGENS',
    name: 'Allergens',
    description: 'Allergen Controls monitor labelling, data completeness, recipe validation, menu checks and failed controls.',
    requiredChecks: ['Allergen label check', 'Recipe allergen data validation'],
    rulesOrThresholds: ['No live menu item without complete allergen data', 'Recipe changes trigger re-validation'],
    evidenceRequirements: ['Allergen label proof', 'Recipe validation sign-off'],
    escalationRules: ['Escalate and block menu publish until allergen data is complete'],
    linkedTasks: ['Menu allergen review task'],
    linkedAudits: ['Allergen Compliance Audit'],
    linkedActionItems: ['Allergen label correction'],
    reportingMetrics: ['Menu items missing allergen data', 'Failed allergen checks'],
  },
  {
    id: 'CA-NUTRITION',
    name: 'Nutrition',
    description: 'Nutrition Controls monitor data completeness, menu suitability, client requirements and standards compliance.',
    requiredChecks: ['Nutrition data completeness check', 'Client requirement suitability check'],
    rulesOrThresholds: ['Nutrition data required before menu goes live for regulated sites (e.g. schools, care)'],
    evidenceRequirements: ['Nutrition data sheet', 'Client standards sign-off'],
    escalationRules: ['Escalate to Nutrition team if client-mandated standard is not met'],
    linkedTasks: ['Nutrition data review task'],
    linkedAudits: ['Nutrition Standards Audit'],
    linkedActionItems: ['Nutrition data completion'],
    reportingMetrics: ['Menu items with incomplete nutrition data'],
  },
  {
    id: 'CA-WASTE',
    name: 'Waste',
    description: 'Waste Controls monitor recording, thresholds, recurring issues, trends and corrective actions.',
    requiredChecks: ['Daily waste recording check', 'Waste threshold check'],
    rulesOrThresholds: ['Waste value should not exceed 5% of food cost per week'],
    evidenceRequirements: ['Waste log', 'Corrective action record'],
    escalationRules: ['Escalate to Site Manager if waste exceeds threshold 2 weeks running'],
    linkedTasks: ['Waste recording task'],
    linkedAudits: ['Waste Management Audit'],
    linkedActionItems: ['Waste reduction corrective action'],
    reportingMetrics: ['Waste value %', 'Repeat waste exception count'],
  },
  {
    id: 'CA-QUALITY',
    name: 'Quality',
    description: 'Quality Controls monitor service quality, presentation, product quality, site standards and recurring failures.',
    requiredChecks: ['Service quality check', 'Presentation standards check'],
    rulesOrThresholds: ['Site standards score must remain above 80%'],
    evidenceRequirements: ['Quality audit score', 'Photo evidence of presentation'],
    escalationRules: ['Escalate to Ops if site standards score drops below threshold twice running'],
    linkedTasks: ['Presentation standards check task'],
    linkedAudits: ['Quality Standards Audit'],
    linkedActionItems: ['Quality corrective action'],
    reportingMetrics: ['Quality score', 'Recurring failure count'],
  },
  {
    id: 'CA-PRICING',
    name: 'Pricing',
    description: 'Pricing Controls monitor approvals, mismatches, margin risk, client rules and exceptions.',
    requiredChecks: ['Price approval check', 'Contract price mismatch check'],
    rulesOrThresholds: ['Price changes above 5% require commercial approval'],
    evidenceRequirements: ['Price approval record', 'Contract rate card'],
    escalationRules: ['Escalate to Commercial if invoiced price differs from contract by more than 2%'],
    linkedTasks: ['Pricing exception review task'],
    linkedAudits: ['Pricing Compliance Audit'],
    linkedActionItems: ['Pricing mismatch resolution'],
    reportingMetrics: ['Pricing mismatch count', 'Margin risk value'],
  },
  {
    id: 'CA-HOSPITALITY',
    name: 'Hospitality',
    description: 'Hospitality & Events Controls monitor event readiness, tasks, missed setup, service issues, feedback and requirements.',
    requiredChecks: ['Event readiness check', 'Setup task completion check'],
    rulesOrThresholds: ['Event setup must be confirmed 24 hours before start'],
    evidenceRequirements: ['Event readiness checklist', 'Client requirement sign-off'],
    escalationRules: ['Escalate to Events Manager if setup not confirmed within 24 hours of event'],
    linkedTasks: ['Event setup task'],
    linkedAudits: ['Hospitality Readiness Audit'],
    linkedActionItems: ['Missed setup corrective action'],
    reportingMetrics: ['Missed setup count', 'Event feedback score'],
  },
  {
    id: 'CA-EVENTS',
    name: 'Events',
    description: 'Hospitality & Events Controls monitor event readiness, tasks, missed setup, service issues, feedback and requirements.',
    requiredChecks: ['Event booking confirmation check', 'Event service issue check'],
    rulesOrThresholds: ['Event bookings require confirmed catering plan 72 hours in advance'],
    evidenceRequirements: ['Event booking confirmation', 'Post-event feedback record'],
    escalationRules: ['Escalate to Events Manager on any unresolved service issue during a live event'],
    linkedTasks: ['Event booking confirmation task'],
    linkedAudits: ['Events Compliance Audit'],
    linkedActionItems: ['Event service issue follow-up'],
    reportingMetrics: ['Event issue count', 'Post-event satisfaction score'],
  },
  {
    id: 'CA-CUSTOMER-EXPERIENCE',
    name: 'Customer Experience',
    description: 'Customer Experience Controls monitor complaints, feedback, service issues, satisfaction and recurring risks.',
    requiredChecks: ['Complaint resolution check', 'Satisfaction survey review'],
    rulesOrThresholds: ['Complaints must be acknowledged within 24 hours'],
    evidenceRequirements: ['Complaint log', 'Resolution record'],
    escalationRules: ['Escalate to Customer Experience lead on repeat complaints from the same site'],
    linkedTasks: ['Complaint acknowledgement task'],
    linkedAudits: ['Customer Experience Audit'],
    linkedActionItems: ['Complaint resolution follow-up'],
    reportingMetrics: ['Complaint count', 'Satisfaction score', 'Repeat complaint rate'],
  },
];
